import { ACCOUNT_PURGE_DAYS, ANON_EXPIRY_DAYS, EXPORT_KEEP_DAYS, INVITE_TTL_DAYS, LOCATION_EXPIRE_MS, MEDIA_EXPIRE_UNVIEWED_DAYS, MESSAGE_TEXT_TTL_DAYS, RECENTS_TTL_DAYS, REMOVED_KEEP_YEARS, SAFETY_KEEP_YEARS, VET_CAPTURE_TTL_DAYS, WALL_SAVE_DAYS, } from './constants.js';
import { ERROR_KEEP_DAYS } from './error-log.js';
const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;
export const JOB_EVERY_MIN_MS = 10_000;
export const JOB_EVERY_MAX_MS = 7 * DAY;
/**
 * Jobs the worker no longer has, whose schedule may still sit in the queue from before: taken off at
 * start. `gate.archive` timed out vouches unvetted (decision 151: a vouch never lapses on its own);
 * `vouch.ask.expire` ended Vouch for me after a week (decision 197: it lasts until they turn it off).
 */
export const RETIRED_JOBS = ['gate.archive', 'vouch.ask.expire'];
export const JOB_CONTROLS = [
    {
        name: 'meets.sweep',
        label: 'Meet timers',
        does: 'Lapses plans nobody answered, asks Still on? before a later meet, asks the one left waiting Did they show? when someone goes quiet, and deletes a meet 30 days after it ends (§5.17).',
        group: 'upkeep',
        defaultEveryMs: MIN,
        params: [],
        note: 'Off, plans stay open past their time and nobody is asked anything until it runs again.',
    },
    {
        name: 'messages.expire',
        label: 'Expire messages',
        does: 'Deletes chat messages, and their photos and videos, once their time is up (in a DM it starts when they see it); and Recents uploads nobody sent.',
        group: 'deletes',
        defaultEveryMs: MIN,
        params: [
            {
                key: 'job.messages.text_days',
                label: 'Messages last',
                unit: 'days',
                default: MESSAGE_TEXT_TTL_DAYS,
                min: 1,
                max: 3650,
                applies: 'new',
            },
            {
                key: 'job.messages.media_unviewed_days',
                label: 'An unopened photo or video lasts',
                unit: 'days',
                default: MEDIA_EXPIRE_UNVIEWED_DAYS,
                min: 1,
                max: 3650,
                applies: 'new',
            },
            {
                key: 'job.messages.recents_days',
                label: 'Recents uploads last',
                unit: 'days',
                default: RECENTS_TTL_DAYS,
                min: 1,
                max: 3650,
                applies: 'new',
            },
        ],
        note: "Off, nothing expires; switched back on, whatever's past its time goes on the next run.",
    },
    {
        name: 'events.purge',
        label: 'Purge past events and closed groups',
        does: "Deletes an event a while after it's over (ended, called off or taken down) and a group a while after it closed or was taken down: its guest list or members, its photo, and its chat. Never while a report on it is open.",
        group: 'deletes',
        defaultEveryMs: DAY,
        params: [
            {
                key: 'job.events.keep_days',
                label: 'Past events and closed groups are kept',
                unit: 'days',
                default: 30,
                min: 1,
                max: 3650,
                applies: 'now',
            },
        ],
    },
    {
        name: 'errors.purge',
        label: 'Delete old errors',
        does: 'Deletes the error log (console › Errors) past its time: each occurrence and its hourly count, and an error not seen since. An issue filed on GitHub stays there.',
        group: 'deletes',
        defaultEveryMs: DAY,
        params: [
            {
                key: 'job.errors.keep_days',
                label: 'Errors are kept',
                unit: 'days',
                default: ERROR_KEEP_DAYS,
                min: 1,
                max: 365,
                applies: 'now',
            },
        ],
    },
    {
        name: 'events.repeat',
        label: 'Make the dates of repeating events',
        does: "Makes each repeating event's next date once the one before it is over or called off, an event of its own with its own guest list and chat, and asks everyone on the last one again.",
        group: 'upkeep',
        defaultEveryMs: 5 * MIN,
        params: [],
        note: 'Off, repeating events stop getting new dates once the one already made is over.',
    },
    {
        name: 'wall.purge',
        label: 'Purge the Wall',
        does: 'Deletes Wall posts a while after they came down (deleted, moderated, or the poster left the map), and saved posts past their time.',
        group: 'deletes',
        defaultEveryMs: DAY,
        params: [
            {
                key: 'job.wall.withdrawn_keep_days',
                label: 'Posts that came down are kept',
                unit: 'days',
                default: 7,
                min: 1,
                max: 3650,
                applies: 'now',
            },
            {
                key: 'job.wall.save_days',
                label: 'Saved posts last',
                unit: 'days',
                default: WALL_SAVE_DAYS,
                min: 1,
                max: 3650,
                applies: 'new',
            },
        ],
    },
    {
        name: 'presence.sweep',
        label: 'Sweep presence',
        does: "Marks people offline and their pins stale; takes a pin off the map, and the person's Wall posts down, once the app hasn't reported in a while. Not in a growing city (unlimited invites there or everywhere): there a pin stays up, offline, until its person goes ghost, turns location off or signs out.",
        group: 'deletes',
        defaultEveryMs: 30_000,
        params: [
            {
                key: 'job.presence.location_expire_hours',
                label: 'Outside growing cities, a pin leaves the map after no report for',
                unit: 'hours',
                default: LOCATION_EXPIRE_MS / HOUR,
                min: 1,
                max: 24 * 365,
                applies: 'now',
            },
        ],
        note: 'Off or slowed down, people show as online and on the map long after they leave.',
    },
    {
        name: 'anon.expire',
        label: 'Expire idle Anonymous accounts',
        does: 'Deletes Anonymous accounts nobody has used in a while (warning them first); accounts.purge clears the rest later.',
        group: 'deletes',
        defaultEveryMs: 6 * HOUR,
        params: [
            {
                key: 'anon.expiry_days',
                label: 'An idle Anonymous account lasts',
                unit: 'days',
                default: ANON_EXPIRY_DAYS,
                min: 1,
                max: 3650,
                applies: 'now',
            },
        ],
    },
    {
        name: 'accounts.purge',
        label: 'Purge deleted and removed accounts',
        does: 'Erases what a deleted or removed account left (profile, health answers, photos, albums, messages, notes both ways, check-ins, tips; bug reports stay, from a deleted user) after a grace period, and old data exports. A removed account keeps only its email and the hashes of its devices and photos, to stop it coming back, until those are cleared too.',
        group: 'deletes',
        defaultEveryMs: DAY,
        params: [
            {
                key: 'job.accounts.purge_days',
                label: 'A deleted account is erased after',
                unit: 'days',
                default: ACCOUNT_PURGE_DAYS,
                min: 1,
                max: 3650,
                applies: 'now',
            },
            {
                key: 'job.accounts.export_keep_days',
                label: 'A data export can be downloaded for',
                unit: 'days',
                default: EXPORT_KEEP_DAYS,
                min: 1,
                max: 365,
                applies: 'now',
            },
            {
                key: 'job.accounts.removed_keep_years',
                label: "A removed account's email, device and photo hashes are kept for",
                unit: 'years',
                default: REMOVED_KEEP_YEARS,
                min: 1,
                max: 20,
                applies: 'now',
            },
        ],
        note: 'A removed account with an appeal still open waits until the appeal is decided.',
    },
    {
        name: 'safety.retention',
        label: 'Delete old safety records',
        does: 'Deletes reports (with any messages and files kept as evidence), moderation decisions, appeals, cascade records, vetter strikes and the audit entries about members, once the case closed long enough ago.',
        group: 'deletes',
        defaultEveryMs: DAY,
        params: [
            {
                key: 'job.safety.keep_years',
                label: 'Safety records are kept, after the case closes, for',
                unit: 'years',
                default: SAFETY_KEEP_YEARS,
                min: 1,
                max: 20,
                applies: 'now',
            },
        ],
        note: 'Switch it off to hold everything for a legal claim, an investigation or a legal hold (Privacy Policy §10); nothing is deleted while it is off.',
    },
    {
        name: 'vet.expire',
        label: 'Expire vet clips',
        does: 'Deletes vet clips nobody reviewed in time, and retries deleting clips of decided vets.',
        group: 'deletes',
        defaultEveryMs: HOUR,
        params: [
            {
                key: 'job.vet.capture_ttl_days',
                label: 'Unreviewed vet clips last',
                unit: 'days',
                default: VET_CAPTURE_TTL_DAYS,
                min: 1,
                max: 365,
                applies: 'new',
            },
        ],
    },
    {
        name: 'invites.expire',
        label: 'Expire invites',
        does: 'Revokes invite links nobody used in time and gives the inviter the invite back.',
        group: 'deletes',
        defaultEveryMs: HOUR,
        params: [
            {
                key: 'job.invites.ttl_days',
                label: 'An invite link lasts',
                unit: 'days',
                default: INVITE_TTL_DAYS,
                min: 1,
                max: 365,
                applies: 'new',
            },
        ],
    },
    {
        name: 'status.expire',
        label: 'End statuses and check-ins',
        does: 'Ends statuses and spot check-ins past their time, and updates the map.',
        group: 'upkeep',
        defaultEveryMs: MIN,
        params: [],
        note: 'Off, statuses and check-ins stay up past their time.',
    },
    {
        name: 'restrictions.expire',
        label: 'Lift restrictions',
        does: 'Gives restricted and suspended members their standing back when the time a moderator set runs out.',
        group: 'upkeep',
        defaultEveryMs: MIN,
        params: [],
    },
    {
        name: 'vet.unlock',
        label: 'Unlock vet reviews',
        does: 'Puts a vet back in the queue when a reviewer opened it and walked away.',
        group: 'upkeep',
        defaultEveryMs: MIN,
        params: [],
    },
    {
        name: 'sla.watch',
        label: 'Watch report deadlines',
        does: 'Marks reports and appeals past their deadline and pages the moderators.',
        group: 'upkeep',
        defaultEveryMs: MIN,
        params: [],
    },
    {
        name: 'spots.activity',
        label: 'Cool spot activity',
        does: "Lowers each spot's activity score a little every run; check-ins raise it. Nothing is deleted.",
        group: 'upkeep',
        defaultEveryMs: HOUR,
        params: [],
    },
    {
        name: 'density.sample',
        label: 'Count the map',
        does: 'Counts how many people are on the map in each region, for Metrics (busy-night numbers). Counts only, no names.',
        group: 'upkeep',
        defaultEveryMs: 15 * MIN,
        params: [
            {
                key: 'job.density.keep_days',
                label: 'Counts are kept for',
                unit: 'days',
                default: 400,
                min: 7,
                max: 3650,
                applies: 'now',
            },
        ],
        note: 'Off or slowed down, the busy-night numbers on Metrics have gaps.',
    },
    {
        name: 'content.announce',
        label: 'Post release announcements',
        does: "Puts each announcement written with a release (content/announcements in the code) behind members' bells, once, as you, for the audience and days it names.",
        group: 'upkeep',
        defaultEveryMs: 15 * MIN,
        params: [],
        note: 'Off, release announcements wait; switched back on, any not posted yet go up on the next run.',
    },
    {
        name: 'content.release',
        label: 'Tell members about a release',
        does: "Once a release is live (it has notes in content/releases), sends every member one notification that it's out, linking to its What's new page. A push goes under their New releases switch.",
        group: 'upkeep',
        defaultEveryMs: 15 * MIN,
        params: [],
        note: "Off, the release notification waits; switched back on, it goes out on the next run, once. Announcements are separate: they're for big features.",
    },
    {
        name: 'media.pins',
        label: 'Make pin photos',
        does: "Makes the small map-pin copy of profile photos that don't have one yet.",
        group: 'upkeep',
        defaultEveryMs: 5 * MIN,
        params: [],
    },
    {
        name: 'media.scan',
        label: 'Scan uploads',
        does: 'Checks each photo, video and vet clip as it is uploaded, then puts it live or rejects it.',
        group: 'on_demand',
        params: [],
    },
    {
        name: 'events.invite',
        label: 'Email event invitations',
        does: "Emails each event invitation where it may go, and makes a moderator's bulk invite (each member invited and told in the app) in batches.",
        group: 'on_demand',
        params: [],
        note: "Off, invitations still land in the app when a host invites someone, but no email goes and a moderator's bulk invite waits.",
    },
    {
        name: 'vet.render',
        label: 'Prepare vet clips',
        does: 'Watermarks vet clips for the reviewer who opens them.',
        group: 'on_demand',
        params: [],
    },
    {
        name: 'push.send',
        label: 'Send push notifications',
        does: 'Sends each push notification as it happens.',
        group: 'on_demand',
        params: [],
    },
    {
        name: 'account.export',
        label: 'Build data exports',
        does: 'Builds the zip a member asks for in Settings.',
        group: 'on_demand',
        params: [],
    },
    {
        name: 'invites.nudge',
        label: 'Nudge invites',
        does: "Reminds members of an invite that's about to lapse, now and then that they can bring people in, and when free Plus they earned is about to end.",
        group: 'upkeep',
        defaultEveryMs: DAY,
        params: [],
    },
    {
        name: 'vouchees.nudge',
        label: 'Remind vouchers of their people',
        does: "Tells a member when someone they vouched for still hasn't sent their clips: at day 2, again more softly at day 7, then never again for that person. One a member a day; the rest wait for the next run.",
        group: 'upkeep',
        defaultEveryMs: DAY,
        params: [],
        note: 'Nothing for anyone who sent their clips, was vetted, removed or deleted, is held or shadow-held, left by Quick Exit, or is blocked either way with their voucher.',
    },
    {
        name: 'away.remind',
        label: 'Remind members who are away',
        does: "Emails a member who hasn't been on for 1, 3, 7 or 14 days, when something new is waiting: unread messages, taps, announcements, new members nearby, looks (Plus). In the early evening where they are, then none until they're back.",
        group: 'upkeep',
        defaultEveryMs: HOUR,
        params: [],
        note: 'Off, nobody is reminded; switched back on, whoever is due gets theirs that evening. Shares the 1,000-a-day limit of your emails to members. The worker needs the SMTP settings to send.',
    },
    {
        name: 'signup.nudge',
        label: 'Remind people to finish signing up',
        does: 'Emails anyone who went quiet for an hour with their profile unfinished, or, Pending, their clips not recorded: then, and once a day after, four at most, each with a link that signs them in.',
        group: 'upkeep',
        defaultEveryMs: 15 * MIN,
        params: [],
        note: 'Off, nobody is reminded; switched back on, whoever is due gets theirs on the next run. The worker needs the SMTP settings to send.',
    },
    {
        name: 'broadcast.send',
        label: 'Send notifications',
        does: "Delivers a notification you send from the console to every member it's for: their Activity, and a push if you chose one.",
        group: 'on_demand',
        params: [],
    },
    {
        name: 'members.email',
        label: 'Email members you picked',
        does: 'Sends your email from console › Users to each member you picked, one message each, a batch a minute, and logs each on their page.',
        group: 'on_demand',
        params: [],
        note: 'Off, nothing goes: a batch due while it is off is skipped, not kept. The worker needs the SMTP settings to send.',
    },
];
export const jobControl = (name) => JOB_CONTROLS.find((j) => j.name === name);
/** The console publishes here after a change; the worker re-applies its schedules. */
export const JOBS_CHANNEL = 'jobs:config';
/** Redis: a job's last run, as the worker saw it (JSON: at, ms, ok, manual, result or error). */
export const jobLastKey = (name) => `job:last:${name}`;
/** The config keys for a job's switch and interval. */
export const jobEnabledKey = (name) => `job.${name}.enabled`;
export const jobEveryKey = (name) => `job.${name}.every_ms`;
/** A retention number's built-in default, by its config key. */
export function jobParamDefault(key) {
    for (const j of JOB_CONTROLS)
        for (const p of j.params)
            if (p.key === key)
                return p.default;
    throw new Error(`unknown job setting ${key}`);
}
/** The keys the API and worker read, by name, so a typo is a type error rather than a silent default. */
export const JOB_PARAM = {
    messageTextDays: 'job.messages.text_days',
    mediaUnviewedDays: 'job.messages.media_unviewed_days',
    recentsDays: 'job.messages.recents_days',
    wallWithdrawnKeepDays: 'job.wall.withdrawn_keep_days',
    wallSaveDays: 'job.wall.save_days',
    eventsKeepDays: 'job.events.keep_days',
    errorsKeepDays: 'job.errors.keep_days',
    locationExpireHours: 'job.presence.location_expire_hours',
    anonExpiryDays: 'anon.expiry_days',
    accountPurgeDays: 'job.accounts.purge_days',
    exportKeepDays: 'job.accounts.export_keep_days',
    removedKeepYears: 'job.accounts.removed_keep_years',
    safetyKeepYears: 'job.safety.keep_years',
    vetCaptureTtlDays: 'job.vet.capture_ttl_days',
    inviteTtlDays: 'job.invites.ttl_days',
    densityKeepDays: 'job.density.keep_days',
};
//# sourceMappingURL=jobs.js.map